/* eslint-disable react/no-unstable-nested-components */
import { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { faLock, faLockOpen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { useAuth } from 'react-oidc-context';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { useUsers } from '@services/query.service';
import { useDeleteUser, useUpdateUserAccess } from '@services/mutation.service';

import AdminTable from '../AdminTable/AdminTable';
import AdminActions from '../AdminActions/AdminActions';
import AdminConfirmationDialog from '../AdminConfirmationDialog/AdminConfirmationDialog';
import AdminStatusBadge from '../AdminStatusBadge/AdminStatusBadge';

import './admin-users.styl';

const columnHelper = createColumnHelper();

function AdminUsers() {
  const { user } = useAuth();
  const { data: users } = useUsers();
  const [deleteUser, setDeleteUser] = useState(null);

  const { t } = useTranslation();

  const updateUserAccessMutation = useUpdateUserAccess();
  const deleteUserMutation = useDeleteUser();

  const columns = [
    columnHelper.accessor(row => `${row.Attributes.given_name} ${row.Attributes.family_name}`, {
      header: t('Name'),
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.Attributes['custom:isApproved'], {
      id: 'isApproved',
      header: t('Status'),
      cell: info => (
        <AdminStatusBadge
          status={info.getValue() === 'true' ? 'access' : 'no access'}
        />
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('Attributes.email', {
      header: t('Email'),
      enableSorting: false,
    }),
    columnHelper.accessor('role', {
      header: t('Role'),
      enableSorting: false,
      cell: info => t(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('Actions'),
      enableSorting: false,
      meta: {
        width: '200px',
      },
      cell: info => (
        <AdminActions
          actions={[
            {
              icon: info.row.original.Attributes['custom:isApproved'] === 'true' ? faLock : faLockOpen,
              title: info.row.original.Attributes['custom:isApproved'] === 'true' ? t('Remove access') : t('Approve access'),
              onClick: () => updateUserAccessMutation.mutate({ userId: info.row.original.Username, isApproved: info.row.original.Attributes['custom:isApproved'] !== 'true' }),
              disabled: info.row.original.Username === user?.profile?.['cognito:username'],
              text: info.row.original.Attributes['custom:isApproved'] === 'true' ? t('Remove access') : t('Approve access'),
            },
            {
              icon: faTrash,
              title: t('Delete user'),
              onClick: () => setDeleteUser(info.row.original),
              iconColor: 'red',
              disabled: info.row.original.Username === user?.profile?.['cognito:username'],
            }]}
        />
      ),
    }),
  ];
  
  const sortedUsers = users?.sort((a, b) => new Date(b.UserCreateDate) - new Date(a.UserCreateDate));

  return (
    <div className="admin-users">
      <h2>
        {t('User management')}
      </h2>
      <div className="admin-users__content">
        <AdminTable
          data={sortedUsers}
          columns={columns}
          initialSorting={[{ id: 'isApproved', desc: false }]}
        />
      </div>
      {deleteUser && (
        <AdminConfirmationDialog
          title={t('Delete user')}
          helpText={t('Do you really want to delete {{userName}}? This action can not be reversed.', { userName: `${deleteUser.Attributes.given_name} ${deleteUser.Attributes.family_name}` })}
          cancelText={t('Cancel')}
          confirmText={t('Yes, delete')}
          onConfirm={async () => {
            await deleteUserMutation.mutateAsync(deleteUser.Username)
            setDeleteUser(null);
          }}
          onCancel={() => setDeleteUser(null)}
          isPending={deleteUserMutation.isPending}
        />
      )}
    </div>
  );
}

export default AdminUsers;